import React from 'react';
import BlueCallToAction from '../components/common/BlueCallToAction';

/**
 * PeopleAreTalkingPage component
 * Showcases industry insights and testimonials about AI in healthcare
 */
const PeopleAreTalkingPage = () => {
  const testimonials = [
    {
      quote: "AI-powered claims processing has reduced our denial rate by 35% and accelerated reimbursement cycles significantly.",
      source: "CFO, Major Health System West",
      publication: "Becker's Hospital Review",
      category: "Revenue Cycle Impact"
    },
    {
      quote: "The implementation of AI in our claims management workflow has improved our first-pass clean claims rate from 75% to 92%.",
      source: "Revenue Cycle Director, Northeast Healthcare Network",
      publication: "Healthcare Financial Management Association",
      category: "Operational Efficiency"
    },
    {
      quote: "Machine learning algorithms are now catching potential claim issues before submission, saving our staff countless hours of rework.",
      source: "Claims Department Manager, Midwest Health Partners",
      publication: "Healthcare Innovation",
      category: "Process Improvement"
    },
    {
      quote: "Our AI system's ability to predict and prevent claim denials has resulted in a 28% increase in clean claims and significantly improved cash flow.",
      source: "VP of Revenue Cycle, Southern Medical Center",
      publication: "Modern Healthcare",
      category: "Financial Performance"
    },
    {
      quote: "The integration of AI into our claims processing has reduced manual review time by 60% while improving accuracy.",
      source: "Operations Director, Pacific Healthcare Group",
      publication: "Health Leaders Media",
      category: "Workflow Optimization"
    },
    {
      quote: "AI-assisted claims management has been transformative - we're seeing faster reimbursements and fewer denials than ever before.",
      source: "Chief Revenue Officer, Eastern Health Network",
      publication: "Healthcare Finance News",
      category: "Revenue Impact"
    }
  ];

  const industryInsights = [
    {
      title: "AI in Healthcare Claims Processing",
      stats: "93% of healthcare leaders report positive ROI from AI implementation in claims management",
      source: "2024 Healthcare AI Adoption Survey",
      icon: "📈"
    },
    {
      title: "Automation Impact",
      stats: "Average 40% reduction in claims processing time with AI-powered systems",
      source: "Healthcare Financial Management Association",
      icon: "⚡"
    },
    {
      title: "Error Reduction",
      stats: "85% decrease in coding errors through AI-assisted validation",
      source: "American Health Information Management Association",
      icon: "✓"
    }
  ];

  return (
    <div className="people-are-talking-page">
      {/* Hero Section */}
      <section className="py-20" style={{
        background: `linear-gradient(to top, #FFC000 0%, #FFFFFF 3%, #FFFFFF 97%, #FFC000 100%)`
      }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-gray-900">
              People Are <span style={{ color: '#FFC000' }}>Talking</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-700">
              Discover how healthcare organizations are transforming their operations
              with AI-powered claims management.
            </p>
          </div>
        </div>
      </section>

      {/* Industry Insights Section */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900 dark:text-white">
            Industry Insights
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {industryInsights.map((insight, index) => (
              <div 
                key={index}
                className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 shadow-sm text-center"
              >
                <div className="text-4xl mb-4">{insight.icon}</div>
                <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                  {insight.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  {insight.stats}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Source: {insight.source}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900 dark:text-white">
            Industry Voices
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm"
              >
                <div className="text-4xl mb-4" style={{ color: '#FFC000' }}>"</div>
                <blockquote className="text-lg mb-6 text-gray-700 dark:text-gray-300">
                  {testimonial.quote}
                </blockquote>
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {testimonial.source}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {testimonial.publication}
                  </p>
                  <span className="inline-block mt-2 text-sm px-3 py-1 rounded-full" style={{ backgroundColor: '#FFF4D9', color: '#0E3056' }}>
                    {testimonial.category}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call-to-Action Section */}
      <BlueCallToAction 
        heading="Join the AI Revolution in Healthcare"
        description="See how our AI solutions can transform your claims management process."
        primaryButtonText="Request Demo"
        primaryButtonLink="/demo"
        secondaryButtonText="Learn More"
        secondaryButtonLink="/solutions"
      />
    </div>
  );
};

export default PeopleAreTalkingPage;


